/**
 * useFilters - Хук для работы с фильтрами
 * ========================================
 * 
 * Централизованный доступ к фильтрам из контекста Dashboard
 */

import React from 'react';
import { useDashboard } from '../contexts/DashboardContext';
import { FiltersService } from '../services/filtersService';

export function useFilters() {
  const context = useDashboard();
  
  // Мемоизируем объект фильтров чтобы избежать бесконечных перерендеров
  const filters = React.useMemo(() => ({
    dateRange: {
      startDate: context.startDate,
      endDate: context.endDate
    },
    project: context.selectedProject,
    campaigns: context.selectedCampaigns,
    mediaSource: context.selectedMediaSource,
    platform: context.selectedPlatform,
    clientVersions: context.selectedClientVersions,
    installVersions: context.selectedInstallVersions,
    
    // Для совместимости со старым кодом
    startDate: context.startDate,
    endDate: context.endDate,
    selectedProject: context.selectedProject,
    selectedCampaigns: context.selectedCampaigns,
    selectedMediaSource: context.selectedMediaSource,
    selectedPlatform: context.selectedPlatform
  }), [
    context.startDate,
    context.endDate,
    context.selectedProject,
    context.selectedCampaigns,
    context.selectedMediaSource,
    context.selectedPlatform,
    context.selectedClientVersions,
    context.selectedInstallVersions
  ]);
  
  // Функции для работы с фильтрами
  const setDateRange = (startDate, endDate) => {
    context.updateDates(startDate, endDate);
  };
  
  const setProject = (project) => {
    context.updateSelectedProject(project);
  };
  
  const setCampaigns = (campaigns) => {
    context.updateSelectedCampaigns(campaigns);
  };
  
  const setMediaSource = (mediaSource) => {
    context.updateSelectedMediaSource(mediaSource);
  };
  
  const setPlatform = (platform) => {
    context.updateSelectedPlatform(platform);
  };
  
  const setApplyPlatformFees = (apply) => {
    context.updateApplyPlatformFees(apply);
  };

  const setIncludeOrganic = (include) => {
    context.updateIncludeOrganic(include);
  };

  const clearFilters = () => {
    context.resetDates();
    context.updateSelectedProject('all');
    context.updateSelectedCampaigns([]);
    context.updateSelectedMediaSource('all');
    context.updateSelectedPlatform('all');
    context.updateSelectedVersions([], []);
    context.updateApplyPlatformFees(false);
    context.updateIncludeOrganic(true);
  };
  
  const setLastNDays = (days) => {
    const filter = FiltersService.createLastNDaysFilter(days);
    setDateRange(filter.dateRange.startDate, filter.dateRange.endDate);
  };
  
  const setCurrentMonth = () => {
    const filter = FiltersService.createCurrentMonthFilter();
    setDateRange(filter.dateRange.startDate, filter.dateRange.endDate);
  };
  
  const setPreviousMonth = () => {
    const filter = FiltersService.createPreviousMonthFilter();
    setDateRange(filter.dateRange.startDate, filter.dateRange.endDate);
  };
  
  return {
    // Текущие фильтры
    filters,
    platform: context.selectedPlatform,
    applyPlatformFees: context.applyPlatformFees,
    
    // Функции установки фильтров
    setDateRange,
    setProject,
    setCampaigns,
    setMediaSource,
    setPlatform,
    setApplyPlatformFees,
    setIncludeOrganic,
    clearFilters,
    
    // Быстрые фильтры
    setLastNDays,
    setCurrentMonth,
    setPreviousMonth,
    
    // Утилиты
    toQueryParams: () => FiltersService.toQueryParams(filters),
    hasActiveFilters: () => FiltersService.hasActiveFilters(filters),
    getDescription: () => FiltersService.getFilterDescription(filters),
    validate: () => FiltersService.validateFilters(filters),
    
    // Доступ к контексту напрямую (для совместимости)
    ...context
  };
}

// Экспортируем для обратной совместимости
export default useFilters;
